// The apps' binding to the catalogue when no account is on screen — T-020's web module (`apps/web/src/app/messages.ts`),
// lifted here by T-369 (D-1246) so the phone renders through the same renderer instead of a hand-copied table of the
// `error.*` sentences it had carried since T-013. Two things: the fallback context for sentences that carry no account
// data — every `error.*` sentence does — and `messageText`, which is `render` with that default and never throws.
//
// A screen inside a shell passes the account's own context instead (the web's `useMessages()`, the phone's
// `useShell().messages`), so money and dates are formatted in the account's currency and zone (REQ-261). A route error
// boundary has no account — it runs when a route throws, possibly before one is loaded — which is what the fallback is for.
import type { Message } from '../types/index';
import { message } from './keys';
import { createMessageContext, render, type MessageContext } from './render';

/** No account: the error sentences have no parameters, so nothing here formats a figure. */
export const fallbackMessageContext: MessageContext = createMessageContext(
  { account: { currency: 'USD', timeZone: 'UTC' }, locale: 'en-US', deviceTimeZone: 'UTC' },
  // Never throw on the fallback path: it is the one that runs when something has already gone wrong.
  { strict: false },
);

/** REQ-287 AC3: what a thrown non-`ApiError` reads as. */
export const GENERIC_ERROR_TEXT: string = render(message('error.unknown'), fallbackMessageContext);

/** Plain English for a `Message`; never the key, never developer text (REQ-287 AC3). */
export function messageText(
  sentence: Message,
  ctx: MessageContext = fallbackMessageContext,
): string {
  try {
    const text = render(sentence, ctx);
    return text === sentence.key ? GENERIC_ERROR_TEXT : text;
  } catch {
    // Strict mode in development: a malformed message must not replace an error screen with a crash.
    return GENERIC_ERROR_TEXT;
  }
}
